import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { patientsApi, patientAttendanceApi, assessmentsApi, invoicesApi, progressApi, protocolsApi } from '../../services/api';
import { formatDateDMY } from '../../utils/dateFormat';
import { usePermissions } from '../../hooks/usePermissions';
import { isForbidden } from '../../utils/accessDenied';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDate, isSunday } from 'date-fns';
import {
  HiOutlineArrowLeft,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineDocumentDownload,
  HiOutlineCurrencyRupee,
  HiPencil,
  HiOutlinePlus,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUser,
  HiX,
  HiEye,
  HiTrash
} from 'react-icons/hi';
import PatientForm from '../../components/Forms/PatientForm';
import TherapyPlanForm from '../../components/Forms/TherapyPlanForm';
import AssessmentForm from '../../components/Forms/AssessmentForm';
import AssessmentDocumentsBlock from '../../components/Assessments/AssessmentDocumentsBlock';
import ExportInvoiceModal from '../../components/Forms/ExportInvoiceModal';
import ProgressReportForm from '../../components/Forms/ProgressReportForm';
import {
  calculateDetailedAgeIST,
  formatCalendarDateDMY,
  formatMonthLongFromIndex0,
  getCalendarNowIST,
  todayYmdIST,
  formatYmdIST,
  formatDateLongOrdinalIST,
  formatDateWeekdayShortIST,
} from '../../utils/timezoneDisplay';

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions();
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState(null);
  const [therapyPlans, setTherapyPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showTherapyPlanForm, setShowTherapyPlanForm] = useState(false);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [reassessmentContext, setReassessmentContext] = useState(null);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Attendance states
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => getCalendarNowIST().month);
  const [selectedYear, setSelectedYear] = useState(() => getCalendarNowIST().year);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [showAssessmentDetail, setShowAssessmentDetail] = useState(false);
  const [detailAssessment, setDetailAssessment] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [downloadingDocxId, setDownloadingDocxId] = useState(null);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [deletingAssessmentId, setDeletingAssessmentId] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [selectedProgressReport, setSelectedProgressReport] = useState(null);
  const [initialProgressReportType, setInitialProgressReportType] = useState('progress_report');
  const [protocols, setProtocols] = useState([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [protocolForm, setProtocolForm] = useState({ protocol_text: '' });
  const [savingProtocol, setSavingProtocol] = useState(false);
  const [deleteProtocolModal, setDeleteProtocolModal] = useState({ open: false, protocol: null });

  const handleDownloadInvoicePdf = async (invoice) => {
    setDownloadingPdfId(invoice.id);
    try {
      const ok = window.confirm('Choose template:\n\nOK = Image template (Header + Footer)\nCancel = Default template');
      const render_style = ok ? 'letterhead' : 'default';
      const response =
        render_style === 'letterhead'
          ? await invoicesApi.getPdf(invoice.id, null, { render_style })
          : await invoicesApi.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      await invoicesApi.logDownload({ invoice_id: invoice.id, download_type: 'pdf', notes_override: null });
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDownloadInvoiceDocx = async (invoice) => {
    setDownloadingDocxId(invoice.id);
    try {
      const ok = window.confirm('Choose template:\n\nOK = Image template (Header + Footer)\nCancel = Default template');
      const render_style = ok ? 'letterhead' : 'default';
      const response = await invoicesApi.getDocx(invoice.id, 'pdf', render_style);
      const serverMime = response.headers?.['content-type'] || 'application/pdf';
      const isPdf = /pdf/i.test(serverMime);
      const blob = new Blob([response.data], { type: serverMime });
      const ext = isPdf ? 'pdf' : 'docx';
      let filename = `Invoice_${invoice.invoice_number}.${ext}`;
      const cd = response.headers?.['content-disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      const fallbackReason = response.headers?.['x-pdf-fallback-reason'];
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      await invoicesApi.logDownload({
        invoice_id: invoice.id,
        download_type: isPdf ? 'pdf' : 'docx',
        notes_override: null,
      });
      if (fallbackReason === 'libreoffice-not-installed') {
        toast('Downloaded as DOCX — install LibreOffice on the server to enable PDF export.', { icon: 'ℹ️', duration: 6000 });
      } else if (fallbackReason) {
        toast('Downloaded as DOCX — PDF conversion failed.', { icon: 'ℹ️', duration: 5000 });
      } else {
        toast.success(isPdf ? 'PDF downloaded' : 'DOCX downloaded');
      }
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingDocxId(null);
    }
  };

  const handleDownloadPatientReportPdf = async (exp, format = 'pdf') => {
    if (!exp?.id) return;
    setDownloadingReportId(exp.id);
    try {
      let render_style = 'default';
      if (format === 'pdf') {
        const ok = window.confirm('Choose template:\n\nOK = Image template (Header + Footer)\nCancel = Default template');
        render_style = ok ? 'letterhead' : 'default';
      }
      const response = await patientsApi.downloadDocxExport(id, exp.id, format, render_style);
      const serverMime = response.headers?.['content-type'] || 'application/pdf';
      const isPdf = /pdf/i.test(serverMime);
      const blob = new Blob([response.data], { type: serverMime });
      const ext = isPdf ? 'pdf' : 'docx';
      let filename = `${patient?.full_name || 'patient'}_${exp.template_code || 'report'}_${exp.receipt_no || 'export'}.${ext}`;
      const cd = response.headers?.['content-disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isPdf ? 'PDF downloaded' : 'DOCX downloaded');
    } catch {
      toast.error('Failed to download report');
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleViewInvoice = async (invoice) => {
    if (!invoice?.id) return;
    setLoadingInvoiceDetail(true);
    setShowInvoiceDetail(true);
    try {
      const response = await invoicesApi.getById(invoice.id);
      setDetailInvoice(response.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load invoice details');
      setShowInvoiceDetail(false);
      setDetailInvoice(null);
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!invoice?.id) return;
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    setDeletingInvoiceId(invoice.id);
    try {
      await invoicesApi.delete(invoice.id);
      toast.success('Invoice deleted');
      if (detailInvoice && String(detailInvoice.id) === String(invoice.id)) {
        setShowInvoiceDetail(false);
        setDetailInvoice(null);
      }
      await fetchPatient();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const handleDeletePatientReport = async (exp) => {
    if (!exp?.id) return;
    if (!window.confirm(`Delete report ${exp.receipt_no || ''}? This cannot be undone.`)) return;
    setDeletingReportId(exp.id);
    try {
      await patientsApi.deleteDocxExport(id, exp.id);
      toast.success('Report deleted');
      await fetchPatient();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete report');
    } finally {
      setDeletingReportId(null);
    }
  };

  useEffect(() => {
    fetchPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canRead]);

  useEffect(() => {
    if (activeTab === 'attendance' && !canRead('attendance')) {
      setActiveTab('overview');
    }
    if (activeTab === 'protocols' && !canRead('protocols')) {
      setActiveTab('overview');
    }
  }, [activeTab, canRead]);

  useEffect(() => {
    if (activeTab === 'attendance' && canRead('attendance')) {
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMonth, selectedYear, canRead]);

  const fetchProtocols = async () => {
    setProtocolsLoading(true);
    try {
      const response = await protocolsApi.getAll({ patient_id: id });
      setProtocols(response.data.data || []);
    } catch {
      toast.error('Failed to fetch protocols');
      setProtocols([]);
    } finally {
      setProtocolsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'protocols' && canRead('protocols')) {
      fetchProtocols();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, canRead]);

  const openCreateProtocolModal = () => {
    setEditingProtocol(null);
    setProtocolForm({ protocol_text: '' });
    setShowProtocolModal(true);
  };

  const openEditProtocolModal = (protocol) => {
    setEditingProtocol(protocol);
    setProtocolForm({ protocol_text: protocol.protocol_text || '' });
    setShowProtocolModal(true);
  };

  const closeProtocolModal = () => {
    setShowProtocolModal(false);
    setEditingProtocol(null);
    setProtocolForm({ protocol_text: '' });
    setSavingProtocol(false);
  };

  const handleProtocolSubmit = async (e) => {
    e.preventDefault();
    if (!protocolForm.protocol_text.trim()) {
      toast.error('Protocol text is required');
      return;
    }
    setSavingProtocol(true);
    try {
      const payload = {
        patient_id: Number(id),
        protocol_text: protocolForm.protocol_text.trim(),
      };
      if (editingProtocol) {
        await protocolsApi.update(editingProtocol.id, payload);
        toast.success('Protocol updated');
      } else {
        await protocolsApi.create(payload);
        toast.success('Protocol added');
      }
      closeProtocolModal();
      await fetchProtocols();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save protocol');
      setSavingProtocol(false);
    }
  };

  const openDeleteProtocolModal = (protocol) => {
    setDeleteProtocolModal({ open: true, protocol });
  };

  const closeDeleteProtocolModal = () => {
    setDeleteProtocolModal({ open: false, protocol: null });
  };

  const handleDeleteProtocol = async () => {
    if (!deleteProtocolModal.protocol) return;
    try {
      await protocolsApi.delete(deleteProtocolModal.protocol.id);
      toast.success('Protocol deleted');
      closeDeleteProtocolModal();
      await fetchProtocols();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete protocol');
    }
  };

  const fetchPatient = async () => {
    try {
      const [patientRes, historyRes, plansRes] = await Promise.all([
        patientsApi.getById(id),
        patientsApi.getHistory(id),
        patientsApi.getTherapyPlans(id),
      ]);
      setPatient(patientRes.data.data);
      setHistory(historyRes.data.data);
      setTherapyPlans(plansRes.data.data);

      if (canRead('attendance')) {
        try {
          const attendanceRes = await patientAttendanceApi.getPatientHistory(id, { limit: 100 });
          if (attendanceRes.data.success) {
            setAttendanceStats(attendanceRes.data.data.stats);
          }
        } catch (e) {
          if (!isForbidden(e)) {
            console.error('Patient attendance summary:', e);
          }
          setAttendanceStats(null);
        }
      } else {
        setAttendanceStats(null);
      }
    } catch (error) {
      toast.error('Failed to fetch patient');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    if (!canRead('attendance')) {
      setAttendanceLoading(false);
      return;
    }
    setAttendanceLoading(true);
    try {
      const response = await patientAttendanceApi.getPatientHistory(id, {
        month: selectedMonth,
        year: selectedYear,
        limit: 100
      });
      if (response.data.success) {
        // Create attendance map by day
        const attendanceMap = {};
        let present = 0, absent = 0, late = 0, excused = 0, holiday = 0;
        
        response.data.data.attendance.forEach(record => {
          const date = new Date(record.attendance_date);
          const recordMonth = date.getMonth() + 1;
          const recordYear = date.getFullYear();
          
          // Only count records from selected month/year
          if (recordMonth === selectedMonth && recordYear === selectedYear) {
            const day = date.getDate();
            attendanceMap[day] = record;
            
            // Count stats for selected month
            switch (record.status) {
              case 'present': present++; break;
              case 'absent': absent++; break;
              case 'late': late++; break;
              case 'excused': excused++; break;
              case 'holiday': holiday++; break;
              default: break;
            }
          }
        });
        
        setAttendanceData(attendanceMap);
        setAttendanceStats(response.data.data.stats);
        setMonthlyStats({
          present,
          absent,
          late,
          excused,
          holiday,
          total: present + absent + late + excused + holiday
        });
      }
    } catch (error) {
      if (!isForbidden(error)) {
        console.error('Failed to fetch attendance:', error);
      }
    } finally {
      setAttendanceLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'absent': return 'bg-rose-100 text-rose-700 border-rose-300';
      case 'late': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'excused': return 'bg-sky-100 text-sky-700 border-sky-300';
      case 'holiday': return 'bg-violet-100 text-violet-700 border-violet-300';
      default: return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'absent': return 'A';
      case 'late': return 'L';
      case 'excused': return 'E';
      case 'holiday': return 'H';
      default: return '-';
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleViewAssessment = async (assessmentId) => {
    setLoadingAssessment(true);
    try {
      const response = await assessmentsApi.getById(assessmentId);
      setDetailAssessment(response.data.data);
      setShowAssessmentDetail(true);
    } catch (error) {
      toast.error('Failed to load assessment details');
    } finally {
      setLoadingAssessment(false);
    }
  };

  const handleEditAssessment = async (assessmentId) => {
    try {
      const response = await assessmentsApi.getById(assessmentId);
      setEditingAssessment(response.data.data);
      setReassessmentContext(null);
      setShowAssessmentForm(true);
    } catch (error) {
      toast.error('Failed to load assessment for editing');
    }
  };

  const handleDeleteAssessment = async (assessment) => {
    if (!assessment?.id) return;
    const label = assessment.is_reassessment ? 're-assessment' : 'assessment';
    const dateLabel = assessment.reassessment_date || assessment.assessment_date || '';
    if (!window.confirm(`Delete this ${label}${dateLabel ? ` (${dateLabel})` : ''}? This cannot be undone.`)) {
      return;
    }
    setDeletingAssessmentId(assessment.id);
    try {
      await assessmentsApi.delete(assessment.id);
      toast.success('Assessment deleted');
      if (detailAssessment && String(detailAssessment.id) === String(assessment.id)) {
        setShowAssessmentDetail(false);
        setDetailAssessment(null);
      }
      await fetchPatient();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete assessment');
    } finally {
      setDeletingAssessmentId(null);
    }
  };

  const handleCreateProgressReport = (reportType = 'progress_report') => {
    setSelectedProgressReport(null);
    setInitialProgressReportType(reportType);
    setShowProgressForm(true);
  };

  const handleEditProgressReport = async (reportId) => {
    try {
      const response = await progressApi.getById(reportId);
      setSelectedProgressReport(response.data.data);
      setInitialProgressReportType(response.data.data?.report_type || 'progress_report');
      setShowProgressForm(true);
    } catch (error) {
      toast.error('Failed to load progress report details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!patient) return null;

  const assignedTherapists = Array.isArray(patient.assigned_therapists)
    ? patient.assigned_therapists
    : [];
  const visibleTherapists = assignedTherapists;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    ...(canRead('attendance') ? [{ id: 'attendance', label: 'Attendance' }] : []),
    { id: 'therapy', label: 'Therapy Plans' },
    { id: 'assessments', label: 'Assessments' },
    { id: 'progress', label: 'Progress Reports' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'reports', label: 'Reports' },
    ...(canRead('protocols') ? [{ id: 'protocols', label: 'Protocol' }] : []),
    { id: 'billing', label: 'Billing' },
  ];

  // Days in month for attendance calendar
  const monthStart = startOfMonth(new Date(selectedYear, selectedMonth - 1));
  const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth - 1));
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/patients')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <HiOutlineArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-gray-900">{patient.full_name}</h1>
            <span className={`badge ${
              patient.status === 'active' 
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {patient.status}
            </span>
          </div>
          <p className="text-primary-600">{patient.patient_code}</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate('invoices') && (
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50"
            >
              <HiOutlineDocumentDownload className="w-5 h-5" />
              Generate Report
            </button>
          )}
          {canUpdate('patients') && (
            <button
              onClick={() => setShowEditForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <HiPencil className="w-5 h-5" />
              Edit Patient
            </button>
          )}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {canRead('attendance') && (
        <button
          onClick={() => setActiveTab('attendance')}
          className="card text-center hover:shadow-lg transition-shadow cursor-pointer"
        >
          <HiOutlineCheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{attendanceStats?.total_present || 0}</p>
          <p className="text-sm text-gray-500">Days Present</p>
        </button>
        )}
        <button
          onClick={() => navigate(`/calendar?patient_id=${id}`)}
          className="card text-center hover:shadow-lg transition-shadow cursor-pointer"
        >
          <HiOutlineCalendar className="w-8 h-8 mx-auto text-primary-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{history?.appointments?.length || 0}</p>
          <p className="text-sm text-gray-500">Appointments</p>
        </button>
        <button
          onClick={() => navigate(`/assessments?patient_id=${id}`)}
          className="card text-center hover:shadow-lg transition-shadow cursor-pointer"
        >
          <HiOutlineDocumentText className="w-8 h-8 mx-auto text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{history?.assessments?.length || 0}</p>
          <p className="text-sm text-gray-500">Assessments</p>
        </button>
        <button
          onClick={() => navigate(`/progress?patient_id=${id}`)}
          className="card text-center hover:shadow-lg transition-shadow cursor-pointer"
        >
          <HiOutlineDocumentText className="w-8 h-8 mx-auto text-green-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{history?.progressReports?.length || 0}</p>
          <p className="text-sm text-gray-500">Progress Reports</p>
        </button>
        <button
          onClick={() => navigate(`/invoices?patient_id=${id}`)}
          className="card text-center hover:shadow-lg transition-shadow cursor-pointer"
        >
          <HiOutlineCurrencyRupee className="w-8 h-8 mx-auto text-purple-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{history?.invoices?.length || 0}</p>
          <p className="text-sm text-gray-500">Invoices</p>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Full Name</span>
                  <span className="font-medium">{patient.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Age</span>
                  <span>{patient.dob && calculateDetailedAgeIST(patient.dob) ? calculateDetailedAgeIST(patient.dob) : (patient.calculated_age || patient.age ? `${patient.calculated_age || patient.age} years` : '-')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Gender</span>
                  <span className="capitalize">{patient.gender || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date of Birth</span>
                  <span>{patient.dob ? formatCalendarDateDMY(patient.dob) : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Enrollment Date</span>
                  <span>{patient.enrollment_date ? formatCalendarDateDMY(patient.enrollment_date) : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span className={`badge ${
                    patient.status === 'active' 
                      ? 'bg-green-100 text-green-700'
                      : patient.status === 'discharged'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {patient.status}
                  </span>
                </div>
                {visibleTherapists.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2">
                      <HiOutlineUser className="w-4 h-4" />
                      Assigned Therapists
                    </span>
                    <div className="mt-2 space-y-2">
                      {visibleTherapists.map((therapist, idx) => (
                        <div key={therapist.id || `${therapist.full_name}-${idx}`} className="bg-gray-50 rounded-lg p-2">
                          <div className="font-medium text-gray-900">{therapist.full_name}</div>
                          {therapist.email && (
                            <div className="text-sm text-gray-500">
                              <a href={`mailto:${therapist.email}`} className="hover:text-primary-600 flex items-center gap-1 mt-1">
                                <HiOutlineMail className="w-3 h-3" />
                                {therapist.email}
                              </a>
                            </div>
                          )}
                          {therapist.phone && (
                            <div className="text-sm text-gray-500">
                              <a href={`tel:${therapist.phone}`} className="hover:text-primary-600 flex items-center gap-1 mt-1">
                                <HiOutlinePhone className="w-3 h-3" />
                                {therapist.phone}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Primary Contact</span>
                  <a href={`tel:${patient.contact_primary}`} className="text-primary-600 hover:underline flex items-center gap-1">
                    <HiOutlinePhone className="w-4 h-4" />
                    {patient.contact_primary}
                  </a>
                </div>
                {patient.contact_whatsapp && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">WhatsApp</span>
                    <a href={`tel:${patient.contact_whatsapp}`} className="text-primary-600 hover:underline">
                      {patient.contact_whatsapp}
                    </a>
                  </div>
                )}
                {patient.email && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Email</span>
                    <a href={`mailto:${patient.email}`} className="text-primary-600 hover:underline flex items-center gap-1">
                      <HiOutlineMail className="w-4 h-4" />
                      {patient.email}
                    </a>
                  </div>
                )}
                {patient.address && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Address</span>
                    <span className="text-right max-w-xs">{patient.address}</span>
                  </div>
                )}
                {patient.emergency_contact && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Emergency Contact</span>
                    <a href={`tel:${patient.emergency_contact}`} className="text-primary-600 hover:underline flex items-center gap-1">
                      <HiOutlinePhone className="w-4 h-4" />
                      {patient.emergency_contact}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {(patient.guardian_name || patient.guardian_relation || patient.guardian_phone) && (
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Guardian Information</h3>
                <div className="space-y-3">
                  {patient.guardian_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Guardian Name</span>
                      <span>{patient.guardian_name}</span>
                    </div>
                  )}
                  {patient.guardian_relation && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Relation</span>
                      <span className="capitalize">{patient.guardian_relation}</span>
                    </div>
                  )}
                  {patient.guardian_phone && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Guardian Phone</span>
                      <a href={`tel:${patient.guardian_phone}`} className="text-primary-600 hover:underline flex items-center gap-1">
                        <HiOutlinePhone className="w-4 h-4" />
                        {patient.guardian_phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(patient.allergies || patient.emergency_contact) && (
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Medical Information</h3>
                <div className="space-y-3">
                  {patient.allergies && (
                    <div>
                      <span className="text-gray-500 text-sm">Allergies</span>
                      <p className="mt-1 whitespace-pre-wrap">{patient.allergies}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {patient.notes && (
              <div className="card lg:col-span-2">
                <h3 className="font-semibold text-gray-900 mb-4">Additional Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{patient.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'attendance' && canRead('attendance') && (
          <div className="space-y-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">Attendance Calendar</h3>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2 py-1">
                <button 
                  onClick={handlePrevMonth}
                  className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <HiOutlineChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-2 py-1 border-0 bg-transparent text-sm font-medium focus:ring-0"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {formatMonthLongFromIndex0(i)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-2 py-1 border-0 bg-transparent text-sm font-medium focus:ring-0"
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
                <button 
                  onClick={handleNextMonth}
                  className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <HiOutlineChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Stats Cards - Shows stats for selected month */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-emerald-700">{monthlyStats?.present || 0}</p>
                <p className="text-xs text-emerald-600">Present</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-rose-700">{monthlyStats?.absent || 0}</p>
                <p className="text-xs text-rose-600">Absent</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-amber-700">{monthlyStats?.late || 0}</p>
                <p className="text-xs text-amber-600">Late</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-sky-700">{monthlyStats?.excused || 0}</p>
                <p className="text-xs text-sky-600">Excused</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-gray-700">{monthlyStats?.total || 0}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="card">
              {attendanceLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-1 mb-2 overflow-x-auto">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 overflow-x-auto">
                    {/* Empty cells for days before month start */}
                    {Array.from({ length: monthStart.getDay() }, (_, i) => (
                      <div key={`empty-${i}`} className="h-10"></div>
                    ))}
                    {/* Actual days */}
                    {daysInMonth.map((day) => {
                      const dayNum = getDate(day);
                      const attendance = attendanceData?.[dayNum];
                      const isSundayDate = isSunday(day);
                      const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                      
                      return (
                        <div
                          key={day.toISOString()}
                          className={`h-10 rounded-md border flex items-center justify-center gap-1 transition-all ${
                            attendance 
                              ? getStatusColor(attendance.status)
                              : isSundayDate 
                                ? 'bg-red-50 border-red-200' 
                                : 'bg-gray-50 border-gray-200'
                          } ${isToday ? 'ring-2 ring-primary-500 ring-offset-1' : ''}`}
                        >
                          <span className={`text-xs ${isSundayDate && !attendance ? 'text-red-500' : ''}`}>
                            {dayNum}
                          </span>
                          {attendance && (
                            <span className="text-sm font-bold">{getStatusLabel(attendance.status)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-3 text-xs">
                    <span className="text-gray-500 font-medium">Legend:</span>
                    <div className="flex items-center gap-1">
                      <span className="w-5 h-5 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold border border-emerald-300">P</span>
                      <span className="text-gray-600">Present</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-5 h-5 rounded bg-rose-100 text-rose-700 flex items-center justify-center font-semibold border border-rose-300">A</span>
                      <span className="text-gray-600">Absent</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-5 h-5 rounded bg-amber-100 text-amber-700 flex items-center justify-center font-semibold border border-amber-300">L</span>
                      <span className="text-gray-600">Late</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-5 h-5 rounded bg-sky-100 text-sky-700 flex items-center justify-center font-semibold border border-sky-300">E</span>
                      <span className="text-gray-600">Excused</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-5 h-5 rounded bg-violet-100 text-violet-700 flex items-center justify-center font-semibold border border-violet-300">H</span>
                      <span className="text-gray-600">Holiday</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'therapy' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Therapy Plans</h3>
              <button
                onClick={() => {
                  setSelectedPlan(null);
                  setShowTherapyPlanForm(true);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <HiOutlinePlus className="w-5 h-5" />
                New Plan
              </button>
            </div>
            {therapyPlans.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No therapy plans</p>
            ) : (
              <div className="space-y-4">
                {therapyPlans.map(plan => (
                  <div key={plan.id} className="p-4 bg-gray-50 rounded-xl hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">
                        {plan.therapy_types && plan.therapy_types.length > 0
                          ? plan.therapy_types.map(t => t.name).join(', ')
                          : plan.therapy_type || 'No therapy type'}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`badge ${
                          plan.status === 'active' 
                            ? 'bg-green-100 text-green-700'
                            : plan.status === 'paused'
                            ? 'bg-amber-100 text-amber-700'
                            : plan.status === 'completed'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {plan.status}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedPlan(plan);
                            setShowTherapyPlanForm(true);
                          }}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                      <div>
                        <p className="text-gray-400">Plan Type</p>
                        <p className="font-medium">{plan.plan_type}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Sessions/Week</p>
                        <p className="font-medium">{plan.sessions_per_week}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Duration</p>
                        <p className="font-medium">{plan.session_duration} mins</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Fee per Session</p>
                        <p className="font-medium">₹{plan.fee_per_session?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    {plan.start_date && (
                      <div className="mt-2 text-sm text-gray-500">
                        <span>Period: {formatCalendarDateDMY(plan.start_date)}</span>
                        {plan.end_date && (
                          <span> - {formatCalendarDateDMY(plan.end_date)}</span>
                        )}
                      </div>
                    )}
                    {plan.doctor_name && (
                      <div className="mt-2 text-sm text-gray-500">
                        Doctor: {plan.doctor_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'assessments' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Assessment History</h3>
              {canCreate('assessments') && (
                <button
                  onClick={() => {
                    const assessments = history?.assessments || [];
                    const parent =
                      assessments.find((item) => !item.is_reassessment) || assessments[0];
                    setReassessmentContext({
                      patient_id: patient.id,
                      parent_assessment_id: parent?.id || null,
                    });
                    setShowAssessmentForm(true);
                  }}
                  className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-2"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  New Re-Assessment
                </button>
              )}
            </div>
            {history?.assessments?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No assessments</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-header">Assessment Date</th>
                      <th className="table-header">Re-Assessment Date</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Doctor</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {history?.assessments?.map((assessment) => (
                      <tr key={assessment.id} className="hover:bg-gray-50">
                        <td className="table-cell">{formatCalendarDateDMY(assessment.assessment_date)}</td>
                        <td className="table-cell">
                          {assessment.reassessment_date ? formatCalendarDateDMY(assessment.reassessment_date) : '-'}
                        </td>
                        <td className="table-cell">
                          {assessment.is_reassessment ? (
                            <span className="badge bg-blue-100 text-blue-700">Re-Assessment</span>
                          ) : (
                            <span className="badge bg-gray-100 text-gray-700">Assessment</span>
                          )}
                        </td>
                        <td className="table-cell">{assessment.doctor_name || '-'}</td>
                        <td className="table-cell">
                          <span className={`badge ${
                            assessment.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {assessment.status}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              onClick={() => handleViewAssessment(assessment.id)}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              View
                            </button>
                            {canUpdate('assessments') && (
                              <button
                                onClick={() => handleEditAssessment(assessment.id)}
                                className="text-sm text-green-600 hover:text-green-700 font-medium"
                              >
                                Edit
                              </button>
                            )}
                            {canCreate('assessments') && !assessment.is_reassessment && (
                              <button
                                onClick={() => {
                                  setReassessmentContext({
                                    patient_id: patient.id,
                                    parent_assessment_id: assessment.id,
                                  });
                                  setEditingAssessment(null);
                                  setShowAssessmentForm(true);
                                }}
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Create Re-Assessment
                              </button>
                            )}
                            {canDelete('assessments') && (
                              <button
                                onClick={() => handleDeleteAssessment(assessment)}
                                disabled={deletingAssessmentId === assessment.id}
                                className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                              >
                                {deletingAssessmentId === assessment.id ? 'Deleting…' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Upcoming Appointments</h3>
              <Link
                to={`/calendar?patient_id=${id}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                View All Appointments →
              </Link>
            </div>
            {history?.appointments?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No upcoming appointments</p>
            ) : (
              <div className="space-y-3">
                {history?.appointments?.map(apt => {
                  const isToday = String(apt.appointment_date).slice(0, 10) === todayYmdIST();

                  return (
                    <div key={apt.id} className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">
                              {isToday ? 'Today' : formatDateWeekdayShortIST(apt.appointment_date)}
                            </p>
                            {isToday && (
                              <span className="badge bg-primary-100 text-primary-700 text-xs">Today</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            <HiOutlineClock className="w-4 h-4 inline mr-1" />
                            {apt.start_time} {apt.end_time ? `- ${apt.end_time}` : ''}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {apt.type} {apt.therapy_type ? `• ${apt.therapy_type}` : ''} {apt.doctor_name ? `• Dr. ${apt.doctor_name}` : ''}
                          </p>
                          {apt.room && (
                            <p className="text-xs text-gray-400 mt-1">Room: {apt.room}</p>
                          )}
                        </div>
                        <span className={`badge ${
                          apt.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          apt.status === 'rescheduled' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {apt.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Progress Reports</h3>
              <div className="flex items-center gap-3">
                <Link
                  to={`/progress?patient_id=${id}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View All Progress Reports →
                </Link>
                {canCreate('progress') && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCreateProgressReport('progress_report')}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm flex items-center gap-2"
                    >
                      <HiOutlinePlus className="w-4 h-4" />
                      New Progress
                    </button>
                    <button
                      onClick={() => handleCreateProgressReport('home_plan_report')}
                      className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm flex items-center gap-2"
                    >
                      <HiOutlinePlus className="w-4 h-4" />
                      New Home Plan
                    </button>
                  </div>
                )}
              </div>
            </div>
            {history?.progressReports?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No progress reports</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-header">Period Start</th>
                      <th className="table-header">Period End</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {history?.progressReports?.map((report) => (
                      <tr key={report.id} className="hover:bg-gray-50">
                        <td className="table-cell">{report.report_period_start ? formatCalendarDateDMY(report.report_period_start) : '-'}</td>
                        <td className="table-cell">{report.report_period_end ? formatCalendarDateDMY(report.report_period_end) : '-'}</td>
                        <td className="table-cell">
                          <span className="capitalize">
                            {String(report.report_type || 'progress_report').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${
                            report.status === 'approved' ? 'bg-green-100 text-green-700' :
                            report.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            report.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {String(report.status || 'draft').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <Link
                              to={`/progress?patient_id=${id}`}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              View
                            </Link>
                            {canUpdate('progress') && (
                              <button
                                onClick={() => handleEditProgressReport(report.id)}
                                className="text-sm text-green-600 hover:text-green-700 font-medium"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Generated reports</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Attendance and other document exports saved for this patient (not invoices).
                </p>
              </div>
            </div>
            {(history?.docx_exports?.length || 0) === 0 ? (
              <p className="text-gray-500 text-center py-8">No generated reports</p>
            ) : (
              <div className="space-y-4">
                {[...(Array.isArray(history?.docx_exports) ? history.docx_exports : [])]
                  .sort((a, b) =>
                    String(b?.created_at || b?.created_date || '').localeCompare(
                      String(a?.created_at || a?.created_date || '')
                    )
                  )
                  .map((exp) => (
                    <div
                      key={`report-${exp.id}`}
                      className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900">{exp.receipt_no || 'Report'}</p>
                            <span className="badge bg-gray-100 text-gray-700">Report</span>
                            <span className="badge bg-amber-100 text-amber-700">
                              {String(exp.template_code || 'attendance_bifurcation').replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {exp.created_date ? formatDateLongOrdinalIST(exp.created_date) : '—'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">—</p>
                          <span className="badge bg-gray-100 text-gray-700">generated</span>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 flex-wrap">
                        <button
                          onClick={() => handleDownloadPatientReportPdf(exp)}
                          disabled={downloadingReportId === exp.id}
                          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          <HiOutlineDocumentDownload className="w-4 h-4" />
                          {downloadingReportId === exp.id ? 'Downloading...' : 'Download PDF'}
                        </button>
                        <button
                          onClick={() => handleDownloadPatientReportPdf(exp, 'docx')}
                          disabled={downloadingReportId === exp.id}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          <HiOutlineDocumentDownload className="w-4 h-4" />
                          {downloadingReportId === exp.id ? 'Downloading...' : 'Download Word'}
                        </button>
                        {canDelete('invoices') && (
                          <button
                            onClick={() => handleDeletePatientReport(exp)}
                            disabled={deletingReportId === exp.id}
                            className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 disabled:opacity-50"
                            title="Delete report"
                          >
                            <HiTrash className="w-4 h-4" />
                            {deletingReportId === exp.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'protocols' && canRead('protocols') && (
          <div className="card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Patient Protocols</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {canCreate('protocols')
                    ? 'Add and manage protocol notes for this patient.'
                    : 'Read-only protocol notes for this patient.'}
                </p>
              </div>
              {canCreate('protocols') && (
                <button
                  type="button"
                  onClick={openCreateProtocolModal}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium shrink-0"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  Add Protocol
                </button>
              )}
            </div>
            {protocolsLoading ? (
              <div className="flex h-28 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : protocols.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No protocols for this patient yet.</p>
            ) : (
              <ul className="space-y-3">
                {protocols.map((protocol, i) => (
                  <li
                    key={protocol.id}
                    className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 text-sm text-gray-800"
                  >
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>{formatDateDMY(protocol.created_at)}</span>
                      <span>·</span>
                      <span>{protocol.created_by_name}</span>
                      {protocol.updated_at && protocol.updated_by_name ? (
                        <>
                          <span>·</span>
                          <span>
                            Updated {formatDateDMY(protocol.updated_at)} by {protocol.updated_by_name}
                          </span>
                        </>
                      ) : null}
                      {i === 0 ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Latest
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                          Earlier
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-[15px] leading-snug text-gray-900">
                      {protocol.protocol_text}
                    </p>
                    {(canUpdate('protocols') || canDelete('protocols')) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canUpdate('protocols') && (
                          <button
                            type="button"
                            onClick={() => openEditProtocolModal(protocol)}
                            className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                          >
                            Edit
                          </button>
                        )}
                        {canDelete('protocols') && (
                          <button
                            type="button"
                            onClick={() => openDeleteProtocolModal(protocol)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <HiTrash className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Billing Information</h3>
              <Link
                to={`/invoices?patient_id=${id}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                View All Invoices →
              </Link>
            </div>
            {(history?.invoices?.length || 0) === 0 ? (
              <p className="text-gray-500 text-center py-8">No invoices</p>
            ) : (
              <div className="space-y-4">
                {[...(Array.isArray(history?.invoices) ? history.invoices : [])]
                  .sort((a, b) => String(b?.invoice_date || '').localeCompare(String(a?.invoice_date || '')))
                  .map((invoice) => {
                    const isAssessmentInvoice = invoice.payment_type === 'assessment' || invoice.assessment_id;
                    const invoiceType = isAssessmentInvoice ? 'Assessment Fees' : 'Patient Fees';
                  
                    return (
                      <div key={invoice.id} className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                            <span className={`badge ${
                              isAssessmentInvoice 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {invoiceType}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatDateLongOrdinalIST(invoice.invoice_date)}
                          </p>
                          {invoice.assessment_date && (
                            <p className="text-xs text-gray-400 mt-1">
                              Assessment Date: {formatCalendarDateDMY(invoice.assessment_date)}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">₹{parseFloat(invoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <span className={`badge ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {invoice.status}
                          </span>
                        </div>
                      </div>
                      
                      {/* Payment Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                          <p className="text-sm font-medium text-gray-900">₹{parseFloat(invoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Paid Amount</p>
                          <p className="text-sm font-medium text-emerald-600">₹{parseFloat(invoice.paid_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Balance</p>
                          <p className={`text-sm font-medium ${
                            parseFloat(invoice.balance_amount || 0) > 0 
                              ? 'text-rose-600' 
                              : 'text-gray-600'
                          }`}>
                            ₹{parseFloat(invoice.balance_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Payments</p>
                          <p className="text-sm font-medium text-gray-900">
                            {invoice.payment_count || 0} {invoice.payment_count === 1 ? 'payment' : 'payments'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Due Date */}
                      {invoice.due_date && invoice.status !== 'paid' && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Due Date: <span className="font-medium text-gray-700">
                              {formatDateLongOrdinalIST(invoice.due_date)}
                            </span>
                            {formatYmdIST(invoice.due_date) < todayYmdIST() && invoice.status !== 'paid' && (
                              <span className="ml-2 text-xs text-red-600 font-medium">(Overdue)</span>
                            )}
                          </p>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleViewInvoice(invoice)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                        >
                          <HiEye className="w-4 h-4" />
                          View Details
                        </button>
                        {invoice.template_code ? (
                          <button
                            onClick={() => handleDownloadInvoiceDocx(invoice)}
                            disabled={downloadingDocxId === invoice.id}
                            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 disabled:opacity-50"
                          >
                            <HiOutlineDocumentDownload className="w-4 h-4" />
                            {downloadingDocxId === invoice.id ? 'Downloading...' : 'Download PDF'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDownloadInvoicePdf(invoice)}
                            disabled={downloadingPdfId === invoice.id}
                            className="text-sm text-gray-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-50"
                          >
                            <HiOutlineDocumentDownload className="w-4 h-4" />
                            {downloadingPdfId === invoice.id ? 'Downloading...' : 'Download PDF'}
                          </button>
                        )}
                        {canDelete('invoices') && (
                          <button
                            onClick={() => handleDeleteInvoice(invoice)}
                            disabled={deletingInvoiceId === invoice.id}
                            className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 disabled:opacity-50"
                            title="Delete invoice"
                          >
                            <HiTrash className="w-4 h-4" />
                            {deletingInvoiceId === invoice.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Patient Form Modal */}
      {showEditForm && (
        <PatientForm
          patient={patient}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            fetchPatient();
          }}
        />
      )}

      {showTherapyPlanForm && (
        <TherapyPlanForm
          plan={selectedPlan}
          patient={patient}
          onClose={() => {
            setShowTherapyPlanForm(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            setShowTherapyPlanForm(false);
            setSelectedPlan(null);
            fetchPatient();
          }}
        />
      )}

      {showAssessmentForm && (
        <AssessmentForm
          assessment={editingAssessment}
          reassessmentContext={reassessmentContext}
          onClose={() => {
            setShowAssessmentForm(false);
            setReassessmentContext(null);
            setEditingAssessment(null);
          }}
          onSuccess={() => {
            setShowAssessmentForm(false);
            setReassessmentContext(null);
            setEditingAssessment(null);
            fetchPatient();
          }}
        />
      )}

      {showExportModal && (
        <ExportInvoiceModal
          patient={patient}
          onClose={() => setShowExportModal(false)}
          onExported={() => {
            setActiveTab('reports');
            fetchPatient();
          }}
          allowedShapes={['attendance_bifurcation']}
          step1Title="Generate Report"
        />
      )}

      {showProtocolModal && (canCreate('protocols') || canUpdate('protocols')) && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl sm:max-w-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingProtocol ? 'Edit Protocol' : 'Add Protocol'}
              </h3>
              <button type="button" onClick={closeProtocolModal} className="text-gray-400 hover:text-gray-600">
                <HiX className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleProtocolSubmit} className="space-y-4 p-4 sm:p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Patient</label>
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900">
                  {patient.full_name} ({patient.patient_code})
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Protocol</label>
                <textarea
                  rows={6}
                  value={protocolForm.protocol_text}
                  onChange={(e) => setProtocolForm({ protocol_text: e.target.value })}
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter patient protocol..."
                  required
                />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={closeProtocolModal}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50 sm:py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProtocol}
                  className="rounded-lg bg-primary-600 px-4 py-2.5 text-white hover:bg-primary-700 disabled:opacity-60 sm:py-2"
                >
                  {savingProtocol ? 'Saving...' : editingProtocol ? 'Update Protocol' : 'Save Protocol'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteProtocolModal.open && canDelete('protocols') && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-xl">
            <div className="border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">Delete Protocol</h3>
              <p className="mt-1 text-sm text-gray-500">
                Are you sure you want to delete this protocol for{' '}
                <span className="font-medium">{patient.full_name}</span>?
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end sm:gap-3 sm:p-5">
              <button
                type="button"
                onClick={closeDeleteProtocolModal}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteProtocol}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showProgressForm && (
        <ProgressReportForm
          report={selectedProgressReport}
          patient={patient}
          initialReportType={initialProgressReportType}
          onClose={() => {
            setShowProgressForm(false);
            setSelectedProgressReport(null);
            setInitialProgressReportType('progress_report');
          }}
          onSuccess={() => {
            setShowProgressForm(false);
            setSelectedProgressReport(null);
            setInitialProgressReportType('progress_report');
            fetchPatient();
          }}
        />
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceDetail && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button
                type="button"
                onClick={() => {
                  setShowInvoiceDetail(false);
                  setDetailInvoice(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            {loadingInvoiceDetail || !detailInvoice ? (
              <div className="flex justify-center py-16">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                      <p className="text-gray-900 font-medium">{detailInvoice.invoice_number}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Invoice Date</label>
                      <p className="text-gray-900">{formatCalendarDateDMY(detailInvoice.invoice_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Patient</label>
                      <p className="text-gray-900 font-medium">
                        {detailInvoice.patient_name}
                        {detailInvoice.patient_code ? ` (${detailInvoice.patient_code})` : ''}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Payment Type</label>
                      <p className="text-gray-900 capitalize">{detailInvoice.payment_type || '—'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <span
                        className={`inline-block mt-1 text-xs px-3 py-1.5 rounded-lg font-medium ${
                          detailInvoice.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : detailInvoice.status === 'overdue'
                              ? 'bg-red-100 text-red-700'
                              : detailInvoice.status === 'partial'
                                ? 'bg-amber-100 text-amber-700'
                                : detailInvoice.status === 'sent'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {detailInvoice.status}
                      </span>
                    </div>
                    {detailInvoice.template_code && (
                      <div className="sm:col-span-2">
                        <label className="text-sm font-medium text-gray-500">Template</label>
                        <p className="text-gray-900">{detailInvoice.template_code}</p>
                      </div>
                    )}
                    {detailInvoice.due_date && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Due Date</label>
                        <p className="text-gray-900">{formatCalendarDateDMY(detailInvoice.due_date)}</p>
                      </div>
                    )}
                  </div>

                  {detailInvoice.items && detailInvoice.items.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Items</label>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Description</th>
                              <th className="px-3 py-2 text-right">Qty</th>
                              <th className="px-3 py-2 text-right">Rate</th>
                              <th className="px-3 py-2 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {detailInvoice.items.map((item, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2">{item.description}</td>
                                <td className="px-3 py-2 text-right">{item.quantity}</td>
                                <td className="px-3 py-2 text-right">
                                  ₹{parseFloat(item.unit_price).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  ₹{parseFloat(item.amount).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">
                        ₹{parseFloat(detailInvoice.subtotal || 0).toFixed(2)}
                      </span>
                    </div>
                    {parseFloat(detailInvoice.tax_amount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium">
                          ₹{parseFloat(detailInvoice.tax_amount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {parseFloat(detailInvoice.discount_amount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Discount:</span>
                        <span className="font-medium text-red-600">
                          -₹{parseFloat(detailInvoice.discount_amount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-300">
                      <span className="font-semibold">Total Amount:</span>
                      <span className="font-bold text-lg text-primary-600">
                        ₹{parseFloat(detailInvoice.total_amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Paid:</span>
                      <span className="font-medium text-green-600">
                        ₹{parseFloat(detailInvoice.paid_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Balance:</span>
                      <span className="font-medium text-amber-600">
                        ₹{parseFloat(detailInvoice.balance_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {detailInvoice.notes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Notes</label>
                      <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailInvoice.notes}</p>
                    </div>
                  )}

                  {detailInvoice.payments && detailInvoice.payments.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Payment History</label>
                      <div className="space-y-2">
                        {detailInvoice.payments.map((payment, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium">{payment.receipt_number}</span>
                              <span className="font-semibold text-green-600">
                                ₹{parseFloat(payment.amount).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>{formatCalendarDateDMY(payment.payment_date)}</span>
                              <span className="capitalize">{payment.payment_mode}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row flex-wrap justify-end gap-2">
                  {detailInvoice.template_code ? (
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoiceDocx(detailInvoice)}
                      disabled={downloadingDocxId === detailInvoice.id}
                      className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <HiOutlineDocumentDownload className="w-4 h-4" />
                      {downloadingDocxId === detailInvoice.id ? 'Downloading...' : 'Download PDF'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoicePdf(detailInvoice)}
                      disabled={downloadingPdfId === detailInvoice.id}
                      className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <HiOutlineDocumentDownload className="w-4 h-4" />
                      {downloadingPdfId === detailInvoice.id ? 'Downloading...' : 'Download PDF'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowInvoiceDetail(false);
                      setDetailInvoice(null);
                    }}
                    className="px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assessment Detail Modal */}
      {showAssessmentDetail && detailAssessment && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Assessment Details</h3>
              <button
                onClick={() => {
                  setShowAssessmentDetail(false);
                  setDetailAssessment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Patient</label>
                  {detailAssessment.patient_name ? (
                    <p className="text-gray-900 font-medium">{detailAssessment.patient_name} ({detailAssessment.patient_code})</p>
                  ) : (
                    <p className="text-gray-400 italic">No patient assigned</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Assessment Date</label>
                  <p className="text-gray-900">{detailAssessment.assessment_date ? formatCalendarDateDMY(detailAssessment.assessment_date) : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="text-gray-900">{detailAssessment.is_reassessment ? 'Re-Assessment' : 'Assessment'}</p>
                </div>
                {detailAssessment.is_reassessment ? (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Home Plan</label>
                    <p className="text-gray-900">{detailAssessment.give_home_plan ? 'Yes' : 'No'}</p>
                  </div>
                ) : null}
                {detailAssessment.reassessment_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Re-Assessment Date</label>
                    <p className="text-gray-900">{formatCalendarDateDMY(detailAssessment.reassessment_date)}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Doctor</label>
                  <p className="text-gray-900">{detailAssessment.doctor_name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`badge ${
                    detailAssessment.status === 'completed' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {detailAssessment.status}
                  </span>
                </div>
                {detailAssessment.therapy_suggestion && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Therapy Suggestion</label>
                    <p className="text-gray-900">{detailAssessment.therapy_suggestion}</p>
                  </div>
                )}
              </div>

              {/* Chief Complaints */}
              {detailAssessment.chief_complaints && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Chief Complaints</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.chief_complaints}</p>
                </div>
              )}

              {/* Medical History */}
              {detailAssessment.medical_history && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Medical History</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.medical_history}</p>
                </div>
              )}

              {/* Observations */}
              {detailAssessment.observations && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Observations</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.observations}</p>
                </div>
              )}

              {/* Recommendations */}
              {detailAssessment.recommendations && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Recommendations</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.recommendations}</p>
                </div>
              )}

              {/* Diagnoses */}
              {detailAssessment.diagnoses && detailAssessment.diagnoses.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Diagnoses</label>
                  <div className="mt-2 space-y-2">
                    {detailAssessment.diagnoses.map((diag, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-sm">
                          {diag.code ? `${diag.code} - ` : ''}{diag.diagnosis_name || diag.custom_diagnosis}
                        </p>
                        {diag.notes && <p className="text-xs text-gray-500 mt-1">{diag.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee Information */}
              {!detailAssessment.is_reassessment && detailAssessment.fee !== undefined && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Fee Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Assessment Fee</label>
                      <p className="text-gray-900">₹{parseFloat(detailAssessment.fee || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Fee Status</label>
                      <p className="text-gray-900">
                        {detailAssessment.fee_waived ? (
                          <span className="badge bg-amber-100 text-amber-700">Waived</span>
                        ) : (
                          <span className="badge bg-green-100 text-green-700">Not Waived</span>
                        )}
                      </p>
                    </div>
                    {detailAssessment.fee_waived && detailAssessment.waiver_reason && (
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Waiver Reason</label>
                        <p className="text-gray-900">{detailAssessment.waiver_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {detailAssessment.notes && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Additional Notes</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.notes}</p>
                </div>
              )}
              {detailAssessment.is_reassessment &&
                (detailAssessment.give_home_plan || detailAssessment.home_plan_text) ? (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Suggestion</label>
                  <div className="text-gray-900 mt-2 prose prose-sm max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: detailAssessment.home_plan_text || '' }} />
                  </div>
                </div>
              ) : null}
              <AssessmentDocumentsBlock assessment={detailAssessment} />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              {canDelete('assessments') && detailAssessment && (
                <button
                  onClick={() => handleDeleteAssessment(detailAssessment)}
                  disabled={deletingAssessmentId === detailAssessment.id}
                  className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 mr-auto"
                >
                  {deletingAssessmentId === detailAssessment.id ? 'Deleting…' : 'Delete'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowAssessmentDetail(false);
                  setDetailAssessment(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetail;


